import React, { FunctionComponent } from 'react';
import { faCopy, faBan, faTrash, faPen } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import PetTypeTags from 'components/UI/V3/PetTypeTags';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { Service, isDaycareService } from 'model/Service';
import styles from './DaycareServiceRow.module.scss';
import { MarketplaceTax } from '@spike/marketplace-model';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface DaycareServiceRowProps {
    service: Service;
    isLast: boolean;
    isMobile: boolean;
    onEdit?: (service: Service) => void;
    onDuplicate?: (service: Service) => void;
    onDeactivate?: (service: Service) => void;
    onDelete?: (service: Service) => void;
}

const DaycareServiceRow: FunctionComponent<DaycareServiceRowProps> = ({
    service,
    isLast,
    isMobile,
    onEdit,
    onDuplicate,
    onDeactivate,
    onDelete
}) => {
    const { getOrganizationMarketplace } = useOrganizationMarketplaces();

    // Access daycare metadata directly from service.daycareData
    const pricingOption = isDaycareService(service) ? service.daycareData.durationOption : 'unknown';

    // Get first price as display price (could be enhanced to show price range)
    const displayPrice = service.pricing.fixedPrice[0]?.price || 0;

    // Use service petTypes directly (already structured)
    const petTypes = service.petTypes;

    const marketplaceId = service.marketplaceId ?? service.marketplaces[0]?.marketplaceId;
    const marketplaceName = marketplaceId != null ? getOrganizationMarketplace(marketplaceId)?.name : undefined;

    // Check if service is active
    const isActive = service.status.id === 1;

    const formatPrice = (price: number) => {
        return `$${price.toFixed(2)}`;
    };

    const getTaxesDisplay = (taxes: Array<MarketplaceTax>) => {
        if (taxes.length === 0) {
            return 'No Taxable';
        }
        return taxes.length === 1 ? `${taxes.length} tax` : `${taxes.length} taxes`;
    };

    const formatPricingOption = (option: string) => {
        switch (option) {
            case 'hourly':
                return 'Hourly';
            case 'half_day':
                return 'Half Day';
            case 'full_day':
                return 'Full Day';
            default:
                return option;
        }
    };

    const editOption = {
        id: 'edit',
        label: 'Edit',
        icon: faPen,
        onClick: () => onEdit?.(service)
    };

    const duplicateOption = {
        id: 'duplicate',
        label: 'Duplicate',
        icon: faCopy,
        onClick: () => onDuplicate?.(service)
    };

    const deactivateOption = {
        id: 'deactivate',
        label: isActive ? 'Deactivate' : 'Activate',
        icon: faBan,
        onClick: () => onDeactivate?.(service)
    };

    const options = [editOption, duplicateOption, deactivateOption];

    const petTypesAndLocation = (
        <div className={styles.petAndLocationContainer}>
            <PetTypeTags petTypes={petTypes} />
            {marketplaceName && (
                <div className={styles.marketplaceContainer}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.marketplaceIcon}
                    />
                    <span className={styles.marketplaceName}>{marketplaceName}</span>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <TableRow className={styles.desktopRow}>
                <TableCell className={styles.nameCell}>
                    <div
                        className={styles.serviceColor}
                        style={{ backgroundColor: service.color || 'transparent' }}
                    ></div>
                    <div className={styles.nameCell}>
                        <p className={styles.serviceName}>{service.name}</p>
                        {petTypesAndLocation}
                    </div>
                </TableCell>

                <TableCell isActions>
                    <ThreeDotsDropdown
                        options={options}
                        direction={isLast ? 'up' : 'down'}
                    />
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={styles.desktopRow}>
            <TableCell className={styles.nameMainCell}>
                <div
                    className={styles.serviceColor}
                    style={{ backgroundColor: service.color || 'transparent' }}
                ></div>
                <div className={styles.nameCell}>
                    <p className={styles.serviceName}>{service.name}</p>
                    {petTypesAndLocation}
                </div>
            </TableCell>

            <TableCell>
                <p>{formatPricingOption(pricingOption)}</p>
            </TableCell>

            <TableCell>
                <p className={styles.price}>{formatPrice(displayPrice)}</p>
            </TableCell>

            <TableCell>
                <p>{getTaxesDisplay(service.pricing.taxes)}</p>
            </TableCell>

            <TableCell isActions>
                <ThreeDotsDropdown
                    options={options}
                    direction={isLast ? 'up' : 'down'}
                />
            </TableCell>
        </TableRow>
    );
};

export default DaycareServiceRow;
